import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import Logo from '../components/Logo'
import { memoryApi } from '../utils/api'

export default function QuantumMemoryQueuePage() {
  const { memoryId } = useParams<{ memoryId: string }>()
  const [queue, setQueue] = useState<any>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!memoryId) return
    const fetch = () => {
      memoryApi.getQueueStatus(memoryId)
        .then(setQueue)
        .catch(e => setError(e.message))
    }
    fetch()
    const interval = setInterval(fetch, 10000) // Poll every 10s
    return () => clearInterval(interval)
  }, [memoryId])

  return (
    <div className="min-h-screen bg-white">
      <header className="h-12 bg-white border-b border-qcloud-border px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="small" />
            <span className="text-lg font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">QuantumArena</span>
          </Link>
          <span className="text-qcloud-muted">/</span>
          <Link to={`/memory/${memoryId}`} className="text-sm text-qcloud-primary hover:underline">Challenge</Link>
          <span className="text-qcloud-muted">/</span>
          <span className="text-sm font-semibold text-qcloud-text">Queue</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Link to={`/memory/${memoryId}`} className="px-3 py-1.5 text-qcloud-muted hover:text-qcloud-primary">Editor</Link>
          <Link to={`/memory/${memoryId}/leaderboard`} className="px-3 py-1.5 text-green-600 font-medium hover:text-green-700">Leaderboard</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-qcloud-text mb-6">Hardware Job Queue</h1>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {queue ? (
          <div className="space-y-6">
            {/* Summary */}
            <div className="flex gap-6">
              <div className="bg-blue-50 rounded-lg px-6 py-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{queue.total_running}</div>
                <div className="text-sm text-blue-500">Running</div>
              </div>
              <div className="bg-amber-50 rounded-lg px-6 py-4 text-center">
                <div className="text-3xl font-bold text-amber-600">{queue.total_queued}</div>
                <div className="text-sm text-amber-500">Queued</div>
              </div>
              {queue.estimated_wait_minutes > 0 && (
                <div className="bg-slate-50 rounded-lg px-6 py-4 text-center">
                  <div className="text-3xl font-bold text-slate-600">{queue.estimated_wait_minutes.toFixed(0)}</div>
                  <div className="text-sm text-slate-500">Est. wait (min)</div>
                </div>
              )}
            </div>

            {/* Running jobs */}
            {queue.running.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Running</h2>
                <div className="space-y-2">
                  {queue.running.map((job: any) => (
                    <div key={job.id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm font-mono">{job.id.slice(0, 8)}...</span>
                        <span className="text-xs text-qcloud-muted">{job.student_label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{job.backend}</span>
                        {job.started_at && <span className="text-qcloud-muted">{new Date(job.started_at).toLocaleTimeString()}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Queued jobs */}
            {queue.queue.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Queued</h2>
                <div className="space-y-2">
                  {queue.queue.map((job: any, i: number) => (
                    <div key={job.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-amber-600">#{job.position || i + 1}</span>
                        <span className="text-sm font-mono">{job.id.slice(0, 8)}...</span>
                        <span className="text-xs text-qcloud-muted">{job.student_label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{job.backend}</span>
                        {job.submitted_at && <span className="text-qcloud-muted">{new Date(job.submitted_at).toLocaleTimeString()}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {queue.total_running === 0 && queue.total_queued === 0 && (
              <div className="text-center py-12 text-qcloud-muted">
                <p className="text-lg">No jobs in queue</p>
                <p className="text-sm mt-2">Submit a job from the <Link to={`/memory/${memoryId}`} className="text-qcloud-primary hover:underline">Editor</Link></p>
              </div>
            )}

            <p className="text-[10px] text-qcloud-muted text-center">Auto-refreshes every 10 seconds</p>
          </div>
        ) : !error ? (
          <div className="text-center py-12 text-qcloud-muted">Loading...</div>
        ) : null}
      </div>
    </div>
  )
}
